import { Avatar, MemberContactPopover, AutoComplete, FieldControl, Dropdown } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';

const Group = styled(Dropdown.Text)`
  font-weight: ${weights.bold};
  font-size: 1rem;
  color: ${colors.grey100};
`;

function ResourceSelect({ value, onChange, ...props }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      let { data } = await api.www.workspaces(workspace.id).allocations().projectSchedule.assignableResources({ q });

      const grouped = data.reduce((a, v) => {
        const key = v.resourceType === 'member' ? 'Members' : 'Resource Placeholders';

        a[key] = a[key] || { key, resources: [] };
        a[key].resources.push(v);
        return a;
      }, {});

      const groups = _.orderBy(Object.values(grouped), 'key');

      const options = [];
      for (const group of groups) {
        options.push({ type: 'group', group, disabled: true });
        group.resources.forEach((resource) => {
          options.push({ type: 'resource', resource });
        });
      }

      return options;
    },
    [workspace.id, api],
  );

  useEffect(() => {
    (async () => {
      const data = await handleSearch('');
      if (!isMounted.current) return;
      setDefaultOptions(data);
    })();
  }, [handleSearch, isMounted]);

  const handleChange = (event) => {
    const changeArgument = { target: { name: event.target.name, value: event.target.value?.resource } };
    onChange(changeArgument);
  };

  return (
    <AutoComplete
      onSearch={handleSearch}
      value={value}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      onChange={handleChange}
      renderValue={(option) => option.resource.name}
      clearable={false}
      {...props}>
      {({ options }) => {
        let index = 0;

        return options.length > 0 ? (
          options.map((o) => {
            switch (o.type) {
              case 'group':
                return (
                  <Group key={o.group.key}>
                    <strong>{o.group.key}</strong>
                  </Group>
                );
              case 'resource':
                return (
                  <AutoComplete.Option padding="0.25rem 0 0.25rem 2rem" key={o.resource.id} value={o} index={index++}>
                    {o.resource.resourceType === 'member' ? (
                      <MemberContactPopover member={o.resource} placement="left">
                        <Avatar value={o.resource} isCircle hasBackground initialsFontSize=".7rem" size="1.5rem" />
                      </MemberContactPopover>
                    ) : null}
                    <span style={{ marginLeft: '.5rem' }}>{o.resource.name}</span>
                  </AutoComplete.Option>
                );
              default:
                return null;
            }
          })
        ) : (
          <Dropdown.Text>None</Dropdown.Text>
        );
      }}
    </AutoComplete>
  );
}

function FieldResourceSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ResourceSelect {...field} {...props} />
    </FieldControl>
  );
}

export default ResourceSelect;
export { FieldResourceSelect };
